import { SOCKET_EVENTS } from "@constants/socket.events";
import { socketService, type ConnectionState } from "../services/socketService";
import { STORAGE_KEYS } from "@config/app.config";
import { getLocalStorage, setLocalStorage } from "@common";
import type {
  SocketContextValue,
  SocketHandlers,
  ConversationStateChangedPayload,
} from "@interfaces/api.interface";

export type { SocketContextValue, SocketHandlers };
interface WebSocketMessage {
  type?: string;
  event?: string;
  data?: unknown;
  [key: string]: unknown;
}

interface SocketServiceLike {
  subscribe(handler: (messageData: unknown) => void): () => void;
  onConnectionStateChange(handler: (state: ConnectionState) => void): () => void;
  send(data: unknown): void;
  connect(token: string): Promise<void>;
  disconnect(): void;
  isConnected(): boolean;
}

class WebSocketWrapper {
  private handlers: SocketHandlers = {};
  private unsubscribeMessageHandler?: () => void;
  private connectionStateUnsubscribers: Map<string, () => void> = new Map();
  private token?: string;
  private socketService: SocketServiceLike;

  get hasActiveListener(): boolean {
    return !!this.unsubscribeMessageHandler;
  }

  constructor(
    token?: string,
    socketServiceInstance: SocketServiceLike = socketService
  ) {
    this.token = token;
    this.socketService = socketServiceInstance;
    if (token) {
      setLocalStorage(STORAGE_KEYS.AUTH_TOKEN, token);
    } else {
      const storedToken = getLocalStorage(STORAGE_KEYS.AUTH_TOKEN);
      if (storedToken) {
        this.token = storedToken;
      } else {
        console.warn(
          "⚠️ WebSocketWrapper: No token provided and none found in localStorage"
        );
      }
    }

    this.unsubscribeMessageHandler = this.socketService.subscribe(
      (messageData: unknown) => {
        this.handleMessage(messageData);
      }
    );
  }

  /**
   * Kiểm tra xem data có phải là message format không
   * Message format: có messageId hoặc id, có conversationId, và không có type
   */
  private isMessageFormat(data: unknown): boolean {
    if (!data || typeof data !== "object" || Array.isArray(data) || data === null) {
      return false;
    }

    const obj = data as Record<string, unknown>;
    const hasMessageId = "messageId" in obj || "id" in obj;
    const hasConversationId = "conversationId" in obj;
    const hasNoType = !("type" in obj);

    return hasMessageId && hasConversationId && hasNoType;
  }

  /**
   * Gọi handler onMessageNew một cách an toàn
   */
  private callMessageNewHandler(message: unknown): void {
    if (!this.handlers.onMessageNew) {
      console.error(
        "❌ WebSocketWrapper: onMessageNew handler is NOT registered!"
      );
      return;
    }

    try {
      this.handlers.onMessageNew(
        message as Parameters<NonNullable<SocketHandlers["onMessageNew"]>>[0]
      );
    } catch (handlerError) {
      console.error(
        "❌ WebSocketWrapper: Error calling onMessageNew handler",
        handlerError
      );
    }
  }

  /**
   * Parse message data từ string hoặc object
   */
  private parseMessage(messageData: unknown): WebSocketMessage | null {
    try {
      const parsed =
        typeof messageData === "string"
          ? JSON.parse(messageData)
          : (messageData as WebSocketMessage);

      console.log("✅ WebSocketWrapper: Message parsed", {
        parsedMessage: parsed,
        hasType: "type" in parsed,
        hasData: "data" in parsed,
        timestamp: new Date().toISOString(),
      });

      return parsed;
    } catch (error) {
      console.warn(
        "❌ WebSocketWrapper: Failed to parse WebSocket message:",
        error
      );
      return null;
    }
  }

  /**
   * Route message đến handler tương ứng dựa trên event type
   */
  private routeMessageByEventType(
    eventType: string | undefined,
    eventData: unknown
  ): void {
    console.log("🔀 WebSocketWrapper: Routing message", {
      eventType: eventType || "undefined",
      eventData,
      hasHandler: !!this.handlers[eventType as keyof SocketHandlers],
      timestamp: new Date().toISOString(),
    });

    switch (eventType) {
      case SOCKET_EVENTS.AUTH:
        this.handlers.onAuth?.(
          eventData as Parameters<NonNullable<SocketHandlers["onAuth"]>>[0]
        );
        break;

      case SOCKET_EVENTS.MESSAGE_NEW:
        this.callMessageNewHandler(eventData);
        break;

      case SOCKET_EVENTS.MESSAGE_UPDATE:
        this.handlers.onMessageUpdate?.(
          eventData as Parameters<
            NonNullable<SocketHandlers["onMessageUpdate"]>
          >[0]
        );
        break;

      case SOCKET_EVENTS.MESSAGE_DELETE:
        this.handlers.onMessageDelete?.(
          eventData as Parameters<
            NonNullable<SocketHandlers["onMessageDelete"]>
          >[0]
        );
        break;

      case SOCKET_EVENTS.MESSAGE_READ:
        this.handlers.onMessageRead?.(
          eventData as Parameters<
            NonNullable<SocketHandlers["onMessageRead"]>
          >[0]
        );
        break;

      case SOCKET_EVENTS.TYPING:
        this.handlers.onTyping?.(
          eventData as Parameters<NonNullable<SocketHandlers["onTyping"]>>[0]
        );
        break;

      case SOCKET_EVENTS.PRESENCE:
        this.handlers.onPresence?.(
          eventData as Parameters<
            NonNullable<SocketHandlers["onPresence"]>
          >[0]
        );
        break;

      case SOCKET_EVENTS.CONVERSATION_UPDATE:
        this.handlers.onConversationUpdate?.(
          eventData as Parameters<
            NonNullable<SocketHandlers["onConversationUpdate"]>
          >[0]
        );
        break;

      case SOCKET_EVENTS.CONVERSATION_NEW:
        this.handlers.onConversationNew?.(
          eventData as Parameters<
            NonNullable<SocketHandlers["onConversationNew"]>
          >[0]
        );
        break;

      case SOCKET_EVENTS.CONVERSATION_STATE_CHANGED:
        this.handlers.onConversationStateChanged?.(
          eventData as Parameters<
            NonNullable<SocketHandlers["onConversationStateChanged"]>
          >[0]
        );
        break;

      default:
        // Nếu không có eventType, kiểm tra xem có phải message format không
        if (!eventType && this.isMessageFormat(eventData)) {
          this.callMessageNewHandler(eventData);
        } else {
          console.log(
            "⚠️ WebSocketWrapper: Unknown event type and not a message format",
            {
              eventType: eventType || "undefined",
              eventData,
              timestamp: new Date().toISOString(),
            }
          );
        }
    }
  }

  private handleMessage(messageData: unknown): void {
    const conversationStateChangedPayload = this.extractConversationStateChanged(messageData);
    if (conversationStateChangedPayload) {
      if (this.handlers.onConversationStateChanged) {
        try {
          this.handlers.onConversationStateChanged(conversationStateChangedPayload);
        } catch (error) {
          console.error("❌ WebSocketWrapper: Error calling onConversationStateChanged handler", error);
        }
      }
      return;
    }

    if (this.isMessageFormat(messageData)) {
      this.callMessageNewHandler(messageData);
      return;
    }

    const parsedMessage = this.parseMessage(messageData);
    if (!parsedMessage) {
      return;
    }

    const eventType = parsedMessage.type || parsedMessage.event;
    const eventData =
      parsedMessage.data !== undefined ? parsedMessage.data : parsedMessage;

    this.routeMessageByEventType(eventType, eventData);
  }

  private extractConversationStateChanged(
    messageData: unknown
  ): ConversationStateChangedPayload | null {
    if (
      !messageData ||
      typeof messageData !== "object" ||
      Array.isArray(messageData) ||
      messageData === null
    ) {
      return null;
    }

    const obj = messageData as Record<string, unknown>;

    if (obj.messageType === "CONVERSATION_STATE_CHANGED") {
      return obj as unknown as ConversationStateChangedPayload;
    }

    if (
      obj.data &&
      typeof obj.data === "object" &&
      !Array.isArray(obj.data)
    ) {
      const nestedData = obj.data as Record<string, unknown>;
      if (nestedData.messageType === "CONVERSATION_STATE_CHANGED") {
        return nestedData as unknown as ConversationStateChangedPayload;
      }
    }

    return null;
  }

  on(event: string, handler: (...args: unknown[]) => void): void {
    switch (event) {
      case SOCKET_EVENTS.AUTH:
        this.handlers.onAuth = handler;
        break;
      case SOCKET_EVENTS.MESSAGE_NEW:
        this.handlers.onMessageNew = handler;
        break;
      case SOCKET_EVENTS.MESSAGE_UPDATE:
        this.handlers.onMessageUpdate = handler;
        break;
      case SOCKET_EVENTS.MESSAGE_DELETE:
        this.handlers.onMessageDelete = handler;
        break;
      case SOCKET_EVENTS.MESSAGE_READ:
        this.handlers.onMessageRead = handler;
        break;
      case SOCKET_EVENTS.TYPING:
        this.handlers.onTyping = handler;
        break;
      case SOCKET_EVENTS.PRESENCE:
        this.handlers.onPresence = handler;
        break;
      case SOCKET_EVENTS.CONVERSATION_UPDATE:
        this.handlers.onConversationUpdate = handler;
        break;
      case SOCKET_EVENTS.CONVERSATION_NEW:
        this.handlers.onConversationNew = handler;
        break;
      case SOCKET_EVENTS.CONVERSATION_STATE_CHANGED:
        this.handlers.onConversationStateChanged = handler;
        break;
      case "connect": {
        const existingUnsubscribe =
          this.connectionStateUnsubscribers.get("connect");
        if (existingUnsubscribe) {
          existingUnsubscribe();
        }
        const unsubscribe = this.socketService.onConnectionStateChange(
          (state: ConnectionState) => {
            if (state === "connected") {
              handler();
            }
          }
        );
        this.connectionStateUnsubscribers.set("connect", unsubscribe);
        break;
      }
      case "disconnect": {
        const existingUnsubscribe =
          this.connectionStateUnsubscribers.get("disconnect");
        if (existingUnsubscribe) {
          existingUnsubscribe();
        }
        const unsubscribe = this.socketService.onConnectionStateChange(
          (state: ConnectionState) => {
            if (state === "disconnected") {
              handler();
            }
          }
        );
        this.connectionStateUnsubscribers.set("disconnect", unsubscribe);
        break;
      }
      case "connect_error": {
        const existingUnsubscribe =
          this.connectionStateUnsubscribers.get("connect_error");
        if (existingUnsubscribe) {
          existingUnsubscribe();
        }
        const unsubscribe = this.socketService.onConnectionStateChange(
          (state: ConnectionState) => {
            if (state === "failed") {
              handler(new Error("Connection failed"));
            }
          }
        );
        this.connectionStateUnsubscribers.set("connect_error", unsubscribe);
        break;
      }
    }
  }

  off(event: string, handler?: (...args: unknown[]) => void): void {
    void handler;
    switch (event) {
      case SOCKET_EVENTS.AUTH:
        this.handlers.onAuth = undefined;
        break;
      case SOCKET_EVENTS.MESSAGE_NEW:
        this.handlers.onMessageNew = undefined;
        break;
      case SOCKET_EVENTS.MESSAGE_UPDATE:
        this.handlers.onMessageUpdate = undefined;
        break;
      case SOCKET_EVENTS.MESSAGE_DELETE:
        this.handlers.onMessageDelete = undefined;
        break;
      case SOCKET_EVENTS.MESSAGE_READ:
        this.handlers.onMessageRead = undefined;
        break;
      case SOCKET_EVENTS.TYPING:
        this.handlers.onTyping = undefined;
        break;
      case SOCKET_EVENTS.PRESENCE:
        this.handlers.onPresence = undefined;
        break;
      case SOCKET_EVENTS.CONVERSATION_UPDATE:
        this.handlers.onConversationUpdate = undefined;
        break;
      case SOCKET_EVENTS.CONVERSATION_NEW:
        this.handlers.onConversationNew = undefined;
        break;
      case SOCKET_EVENTS.CONVERSATION_STATE_CHANGED:
        this.handlers.onConversationStateChanged = undefined;
        break;
      case "connect": {
        const unsubscribe = this.connectionStateUnsubscribers.get("connect");
        if (unsubscribe) {
          unsubscribe();
          this.connectionStateUnsubscribers.delete("connect");
        }
        break;
      }
      case "disconnect": {
        const unsubscribe = this.connectionStateUnsubscribers.get("disconnect");
        if (unsubscribe) {
          unsubscribe();
          this.connectionStateUnsubscribers.delete("disconnect");
        }
        break;
      }
      case "connect_error": {
        const unsubscribe =
          this.connectionStateUnsubscribers.get("connect_error");
        if (unsubscribe) {
          unsubscribe();
          this.connectionStateUnsubscribers.delete("connect_error");
        }
        break;
      }
    }
  }

  emit(event: string, data?: unknown): void {
    this.socketService.send({
      type: event,
      data: data,
    });
  }

  connect(): void {
    const tokenToUse = this.token || getLocalStorage(STORAGE_KEYS.AUTH_TOKEN);
    if (tokenToUse) {
      // console.log(
      //   "🔌 WebSocketWrapper.connect: Using token:",
      //   tokenToUse.substring(0, 20) + "..."
      // );
      this.socketService.connect(tokenToUse).catch((error) => {
        console.error("Failed to connect WebSocket:", error);
      });
    } else {
      console.warn("⚠️ WebSocketWrapper: No token available to connect");
    }
  }

  disconnect(): void {
    this.socketService.disconnect();
  }

  get connected(): boolean {
    return this.socketService.isConnected();
  }

  get currentToken(): string | undefined {
    return this.token;
  }

  get disconnected(): boolean {
    return !this.socketService.isConnected();
  }

  once(event: string, handler: (...args: unknown[]) => void): void {
    const onceHandler = (...args: unknown[]) => {
      handler(...args);
      this.off(event, onceHandler);
    };
    this.on(event, onceHandler);
  }

  cleanup(): void {
    if (this.unsubscribeMessageHandler) {
      this.unsubscribeMessageHandler();
      this.unsubscribeMessageHandler = undefined;
    }
    this.connectionStateUnsubscribers.forEach((unsubscribe) => unsubscribe());
    this.connectionStateUnsubscribers.clear();
    this.handlers = {};
  }

  clearHandlers(): void {
    this.handlers = {};
  }
}

// Public interface for WebSocketWrapper (for type exports)
export interface SocketConnection {
  connected: boolean;
  disconnected: boolean;
  currentToken?: string;
  connect(): void;
  disconnect(): void;
  on(event: string, handler: (...args: unknown[]) => void): void;
  off(event: string, handler?: (...args: unknown[]) => void): void;
  emit(event: string, data?: unknown): void;
  once(event: string, handler: (...args: unknown[]) => void): void;
  cleanup(): void;
  clearHandlers(): void;
}

const socketWrappers = new WeakMap<SocketServiceLike, WebSocketWrapper>();

export const createSocketConnection = (
  token?: string,
  socketServiceInstance: SocketServiceLike = socketService
): SocketConnection => {
  const existingWrapper = socketWrappers.get(socketServiceInstance);
  if (existingWrapper) {
    const currentToken = existingWrapper.currentToken;
    if (currentToken === token) {
      if (!existingWrapper.hasActiveListener) {
        existingWrapper.cleanup();
        if (existingWrapper.connected) {
          existingWrapper.disconnect();
        }
        const wrapper = new WebSocketWrapper(token, socketServiceInstance);
        socketWrappers.set(socketServiceInstance, wrapper);
        return wrapper;
      }
      return existingWrapper;
    }
    existingWrapper.cleanup();
    if (existingWrapper.connected) {
      existingWrapper.disconnect();
    }
  }
  const wrapper = new WebSocketWrapper(token, socketServiceInstance);
  socketWrappers.set(socketServiceInstance, wrapper);
  return wrapper;
};

export const registerSocketHandlers = (
  socket: SocketConnection,
  handlers: SocketHandlers
): void => {

  if (handlers.onAuth) {
    socket.on(
      SOCKET_EVENTS.AUTH,
      handlers.onAuth as (...args: unknown[]) => void
    );
  }
  if (handlers.onMessageNew) {
    socket.on(
      SOCKET_EVENTS.MESSAGE_NEW,
      handlers.onMessageNew as (...args: unknown[]) => void
    );
  }
  if (handlers.onMessageUpdate) {
    socket.on(
      SOCKET_EVENTS.MESSAGE_UPDATE,
      handlers.onMessageUpdate as (...args: unknown[]) => void
    );
  }
  if (handlers.onMessageDelete) {
    socket.on(
      SOCKET_EVENTS.MESSAGE_DELETE,
      handlers.onMessageDelete as (...args: unknown[]) => void
    );
  }
  if (handlers.onMessageRead) {
    socket.on(
      SOCKET_EVENTS.MESSAGE_READ,
      handlers.onMessageRead as (...args: unknown[]) => void
    );
  }
  if (handlers.onTyping) {
    socket.on(
      SOCKET_EVENTS.TYPING,
      handlers.onTyping as (...args: unknown[]) => void
    );
  }
  if (handlers.onPresence) {
    socket.on(
      SOCKET_EVENTS.PRESENCE,
      handlers.onPresence as (...args: unknown[]) => void
    );
  }
  if (handlers.onConversationUpdate) {
    socket.on(
      SOCKET_EVENTS.CONVERSATION_UPDATE,
      handlers.onConversationUpdate as (...args: unknown[]) => void
    );
  }
  if (handlers.onConversationNew) {
    socket.on(
      SOCKET_EVENTS.CONVERSATION_NEW,
      handlers.onConversationNew as (...args: unknown[]) => void
    );
  }
  if (handlers.onConversationStateChanged) {
    socket.on(
      SOCKET_EVENTS.CONVERSATION_STATE_CHANGED,
      handlers.onConversationStateChanged as (...args: unknown[]) => void
    );
  }
};

export const unregisterSocketHandlers = (socket: SocketConnection): void => {
  Object.values(SOCKET_EVENTS).forEach((event) => socket.off(event));
  socket.off("connect");
  socket.off("disconnect");
  socket.off("connect_error");
  
  if (typeof socket.clearHandlers === "function") {
    socket.clearHandlers();
  }
};
